<template>
  <div v-if="organization" class="bg-white rounded-2xl shadow-md p-6">
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
      <!-- Column 1 -->
      <div class="space-y-2">
        <StackedField label="Id">
          <FieldSpan :style-to-add="'bg-purple-600 text-white'" :value="organization.id" />
        </StackedField>
        <StackedField label="Nom">
          <FormField v-if="editMode" id="name" name="name" v-model="formData.name" :placeholder="organization.name" />
          <template v-else>{{ organization.name || "—" }}</template>
        </StackedField>
        <StackedField label="Raison sociale">
          <FormField
            v-if="editMode"
            id="business_name"
            name="business_name"
            v-model="formData.businessName"
            :placeholder="organization.businessName"
          />
          <template v-else>{{ organization.businessName || "—" }}</template>
        </StackedField>
        <StackedField label="Numéro SIRET">
          <FormField
            v-if="editMode"
            id="registration_number"
            name="registration_number"
            v-model="formData.registrationNumber"
            :placeholder="organization.registrationNumber"
          />
          <template v-else>{{ organization.registrationNumber || "—" }}</template>
        </StackedField>
      </div>

      <!-- Column 2 -->
      <div class="space-y-2">
        <StackedField label="Adresse de facturation">
          <FormField
            v-if="editMode"
            id="billing_address"
            name="billing_address"
            v-model="formData.billingAddress"
            :placeholder="organization.billingAddress"
          />
          <template v-else>{{ organization.billingAddress || "—" }}</template>
        </StackedField>
        <StackedField label="Type de réseau">
          <Selector
            v-if="editMode"
            id="network_type"
            name="network_type"
            v-model="formData.networkType"
            :options="networkTypeOptions"
          />
          <template v-else>
            <span v-if="organization.networkType">
              {{ $t(`organization.network_type.${organization.networkType}`) }}
            </span>
            <span v-else>—</span>
          </template>
        </StackedField>
        <StackedField label="CA du réseau">
          <Selector
            v-if="editMode"
            id="revenue_range"
            name="revenue_range"
            v-model="formData.revenueRange"
            :options="revenueRangeOptions"
          />
          <template v-else>
            <span v-if="organization.revenueRange">
              {{ $t(`organization.revenue_range.${organization.revenueRange}`) }}
            </span>
            <span v-else>—</span>
          </template>
        </StackedField>
        <StackedField label="Nombre de points de vente">
          <FormField
            v-if="editMode"
            id="total_points_of_sale"
            name="total_points_of_sale"
            v-model="formData.totalPointsOfSale"
            :placeholder="organization.totalPointsOfSale"
          />
          <template v-else>{{ organization.totalPointsOfSale || "—" }}</template>
        </StackedField>
      </div>

      <!-- Column 3 -->
      <div class="space-y-2">
        <StackedField label="Mode de facturation" :value="$t(`invoice_level.${organization.invoiceLevel}`)" />
        <StackedField label="Source" :value="$t(organization.source)" />
        <StackedField label="IBAN">
          <FormField v-if="ibanEditable" id="iban" name="iban" v-model="formData.iban" />
          <template v-else>{{ organization.iban || "—" }}</template>
        </StackedField>
        <StackedField v-if="editMode" label="Logo">
          <FormField id="logo_url" name="logo_url" v-model="formData.logoUrl" :placeholder="organization.logoUrl" />
        </StackedField>
        <CopyableFieldRow v-else-if="isPresent(organization.logoUrl)" label="Logo" :value="organization.logoUrl" />
        <StackedField v-else label="Logo" value="—" />
        <template v-if="mandate">
          <StackedField label="Mandat de prélèvement" :value="$t(`mandate.status.${mandate.status}`)" />
          <StackedField
            v-if="mandate.responsibleEmail"
            :label="$t('mandate.responsible_email')"
            :value="mandate.responsibleEmail"
          />
          <StackedField v-if="mandateToSetup" label="Lien d'activation du mandat">
            <a
              :href="mandate.authorizationUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue-400 underline"
            >
              Lien
            </a>
          </StackedField>
        </template>
      </div>
    </div>

    <div class="mt-6 pt-4 border-t border-gray-200 flex flex-row-reverse gap-x-4" v-if="editMode">
      <SecondaryButton @click="updateOrganization">Enregistrer</SecondaryButton>
      <SecondaryButton @click="cancelEdit">Annuler</SecondaryButton>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { CopyableFieldRow, FormField, SecondaryButton, Selector, FieldSpan, StackedField, isPresent } from "@estaly/ui";

export default {
  components: {
    CopyableFieldRow,
    FieldSpan,
    FormField,
    SecondaryButton,
    Selector,
    StackedField,
  },

  props: {
    organization: null,
    editMode: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["updated", "update:editMode"],

  data() {
    return {
      formData: { ...this.organization },
    };
  },

  watch: {
    organization: {
      handler() {
        this.formData = { ...this.organization };
      },
      deep: true,
    },
  },

  computed: {
    ...mapGetters("organizations", ["revenueRanges", "networkTypes"]),

    ibanEditable() {
      return this.editMode && this.organization.invoiceLevel === "organization";
    },

    revenueRangeOptions() {
      return this.revenueRanges().map((revenueRange) => ({
        value: revenueRange,
        label: this.$t(`organization.revenue_range.${revenueRange}`),
      }));
    },

    networkTypeOptions() {
      return this.networkTypes().map((networkType) => ({
        value: networkType,
        label: this.$t(`organization.network_type.${networkType}`),
      }));
    },

    mandate() {
      return this.organization.mandate;
    },

    mandateToSetup() {
      return this.mandate?.status === "to_setup";
    },
  },

  methods: {
    isPresent,
    ...mapActions("notifications", ["notify"]),
    ...mapActions("organizations", ["update"]),

    cancelEdit() {
      this.formData = { ...this.organization };
      this.$emit("update:editMode", false);
    },

    async updateOrganization() {
      const response = await this.update({
        id: this.organization.id,
        ...this.formData,
      });

      if (!response?.success) return;

      this.notify({
        category: "simple",
        type: "success",
        title: "Mise à jour effectuée",
      });
      this.$emit("updated");
      this.$emit("update:editMode", false);
    },
  },
};
</script>
